import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { ScrollArea } from '@art-of-hospitality-solutions-llc/ui-kit/components/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/table';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { CloudinaryImage } from '@art-of-hospitality-solutions-llc/ui-kit/components/cloudinary-image';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import {
  RadioGroup,
  RadioGroupItem,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ExternalLink,
  ArchiveIcon,
  ArchiveRestoreIcon,
  Trash2Icon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';
import { z } from 'zod';

import Breadcrumbs from '@Components/Breadcrumbs';
import NotFound from '@Components/NotFound';
import RichText from '@Components/RichText';
import Tiptap from '@Components/TipTap';
import EventPreview from '@Components/TicketedEvents/EventPreview';
import EventsSitePreview from '@Components/TicketedEvents/EventsSitePreview';
import {
  EventArchivedBadge,
  EventBadges,
} from '@Components/TicketedEvents/Badges';
import PhonePreviewContainer from '@Components/PhonePreviewContainer';
import { useEvent } from '@Hooks/useEvent';
import { useVenue } from '@Hooks/useVenue';
import FeaturedImageDialog from '../../../components/FeaturedImageDialog';
import ManageGalleryImagesDialog from '../../../components/ManageGalleryImagesDialog';
import { useEventPreviewStore } from '../../../stores/eventPreviewStore';
import service from '@Service';
import ShowFor from '@Components/ShowFor';
import ArchiveEventDialog from '@Components/TicketedEvents/ArchiveEvent/ArchiveEventDialog';
import RestoreEventDialog from '@Components/TicketedEvents/RestoreEvent/RestoreEventDialog';
import { formatZonedDateTime } from '@Utils/utils';
import { cn } from '@Utils/cn';

const EditDetailsSchema = z
  .object({
    name: z.string().min(2, { message: 'A name is required.' }),
    description: z.string().min(1, { message: 'A description is required.' }),
    teaser: z.string().optional().nullable(),
    startDateTime: z.string().optional().nullable(),
    endDateTime: z.string().optional().nullable(),
    visibility: z.enum(['PUBLIC', 'MEMBERS_ONLY', 'PRIVATE']),
    status: z.enum(['DRAFT', 'PUBLISHED']),
  })
  .refine(
    (data) => {
      const start = data.startDateTime?.trim()
        ? new Date(data.startDateTime).getTime()
        : null;
      const end = data.endDateTime?.trim()
        ? new Date(data.endDateTime).getTime()
        : null;
      if (start !== null && end !== null && end < start) return false;
      return true;
    },
    {
      message: 'End date and time must be after start date and time.',
      path: ['endDateTime'],
    },
  );

type EditDetailsValues = z.infer<typeof EditDetailsSchema>;

const toDateTimeLocal = (isoString: string | null | undefined): string => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const toISO = (dateTimeLocal: string | null | undefined): string | null => {
  if (!dateTimeLocal || !dateTimeLocal.trim()) return null;
  return new Date(dateTimeLocal).toISOString();
};

export default function EventDetails() {
  const { eventId } = useParams();
  const queryClient = useQueryClient();

  const {
    data: event,
    status,
    isError,
  } = useEvent({
    eventId,
    include: 'venue,featuredImage,images,categories,tags,organizers',
  });

  const { data: venue } = useVenue({
    venueId: event?.venueId,
    toastInclude: 'toastRestaurant',
    enabled: !!event?.venueId,
  });

  const [eventDetailsSheetOpen, setEventDetailsSheetOpen] = useState(false);
  const [eventArchiveDialogOpen, setEventArchiveDialogOpen] = useState(false);
  const [eventRestoreDialogOpen, setEventRestoreDialogOpen] = useState(false);
  const [featuredImageDialogOpen, setFeaturedImageDialogOpen] = useState(false);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState<'members-app' | 'events-site'>(
    'members-app',
  );

  const updateImageMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (!eventId) throw new Error('Missing eventId');
      return service.updateEvent(eventId, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });

  const detailsForm = useForm<EditDetailsValues>({
    resolver: zodResolver(EditDetailsSchema),
    defaultValues: {
      name: '',
      description: '',
      teaser: '',
      startDateTime: '',
      endDateTime: '',
      visibility: 'PUBLIC',
      status: 'DRAFT',
    },
  });

  useEffect(() => {
    if (eventDetailsSheetOpen && event) {
      detailsForm.reset({
        name: event.name,
        description: event.description ?? '',
        teaser: event.teaser ?? '',
        startDateTime: toDateTimeLocal(event.startDateTime),
        endDateTime: toDateTimeLocal(event.endDateTime),
        visibility: event.visibility,
        status: event.status,
      });
      detailsForm.clearErrors('root');
    }
  }, [event, eventDetailsSheetOpen, detailsForm]);

  const updateDetailsMutation = useMutation({
    mutationFn: async (values: EditDetailsValues) => {
      if (!eventId) throw new Error('Missing eventId');

      const eventData: Record<string, unknown> = {
        name: values.name,
        description: values.description,
        visibility: values.visibility,
        status: values.status,
      };

      if (values.teaser && values.teaser.trim()) {
        eventData.teaser = values.teaser.trim();
      }

      const startDate = toISO(values.startDateTime);
      if (startDate) eventData.startDateTime = startDate;

      const endDate = toISO(values.endDateTime);
      if (endDate) eventData.endDateTime = endDate;

      return service.updateEvent(eventId, { event: eventData });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
      toastSuccess('Event details updated successfully.');
      setEventDetailsSheetOpen(false);
    },
    onError: (error) => {
      console.error('Error updating event:', error);
      detailsForm.setError('root', {
        type: 'server',
        message:
          'We were unable to update this event. Please check your changes and try again.',
      });
    },
  });

  const onDetailsSubmit = async (values: EditDetailsValues) => {
    detailsForm.clearErrors('root');
    updateDetailsMutation.mutate(values);
  };

  const handleDetailsSheetOpenChange = (newIsOpen: boolean) => {
    setEventDetailsSheetOpen(newIsOpen);
    if (!newIsOpen && event) {
      detailsForm.reset({
        name: event.name,
        description: event.description ?? '',
        teaser: event.teaser ?? '',
        startDateTime: toDateTimeLocal(event.startDateTime),
        endDateTime: toDateTimeLocal(event.endDateTime),
        visibility: event.visibility,
        status: event.status,
      });
      detailsForm.clearErrors('root');
    }
  };

  if (isError) {
    return <NotFound />;
  }
  if (status === 'pending' || !event) {
    return <div>Loading...</div>;
  }

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Events', href: '/events' },
          { name: 'General', href: '/events/general' },
          { name: event.name, href: `/events/${eventId}` },
        ]}
      />

      <div className="mt-8 mb-4">
        <Heading>{event.name}</Heading>
        <p className="mb-1 text-sm font-bold text-zinc-500">
          EVENT AT {venue?.name ? venue?.name.toUpperCase() : ''}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-12 xl:grid-cols-3">
        <div className="mb-12 flex flex-col space-y-8 xl:col-span-2">
          <div className="grid grid-cols-1 gap-8 xl:grid-cols-5">
            <div className="col-span-3">
              <div className="mb-2 flex items-center justify-between">
                <Heading level="h3">Details</Heading>
                <div className="flex items-center space-x-2">
                  <ShowFor permissions={['EVENTS_WRITE']}>
                    {event.archivedAt ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEventRestoreDialogOpen(true)}
                        icon={{
                          src: <ArchiveRestoreIcon className="h-4 w-4" />,
                        }}
                      >
                        Restore
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEventArchiveDialogOpen(true)}
                        icon={{
                          src: <ArchiveIcon className="h-4 w-4" />,
                        }}
                        className="text-red-500 hover:text-red-600"
                      >
                        Archive
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEventDetailsSheetOpen(true)}
                    >
                      Edit
                    </Button>
                  </ShowFor>
                </div>
              </div>
              <Card>
                <CardContent>
                  <div className="relative">
                    <div className="flex flex-col space-y-2.5">
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Name
                        </div>
                        {event.name}
                        <div className="mt-1 flex flex-wrap gap-1">
                          <EventBadges event={event as any} />
                          <EventArchivedBadge event={event as any} />
                        </div>
                      </div>
                      {event.teaser && (
                        <div>
                          <div className="text-sm font-bold text-zinc-500">
                            Teaser
                          </div>
                          {event.teaser}
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-4 md:grid">
                        <div>
                          <div className="text-sm font-bold text-zinc-500">
                            Start Date & Time
                          </div>
                          {formatZonedDateTime(
                            event.startDateTime,
                            "MMMM dd, yyyy 'at' hh:mm a",
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-bold text-zinc-500">
                            End Date & Time
                          </div>
                          {formatZonedDateTime(
                            event.endDateTime,
                            "MMMM dd, yyyy 'at' hh:mm a",
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="mb-2 text-sm font-bold text-zinc-500">
                          Description
                        </div>
                        <div className="rounded-xl bg-zinc-100 p-4">
                          <ScrollArea className="h-96 overflow-auto">
                            <RichText
                              content={event.description}
                              className="!max-w-full"
                            />
                          </ScrollArea>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="col-span-2 space-y-8">
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <Heading level="h3">Location</Heading>
                </div>
                <Card>
                  <CardContent>
                    <div className="flex flex-col space-y-2.5">
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Venue
                        </div>
                        {event.venue && event.venueId ? (
                          <Link to={`/venues/${event.venue.id}`}>
                            {event.venue.name}
                          </Link>
                        ) : (
                          <span className="text-zinc-500">
                            No venue assigned
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="flex flex-col space-y-8">
                <div className="mb-2">
                  <Heading level="h3">Photos</Heading>
                </div>
                <div>
                  <Card>
                    <CardContent>
                      <div className="space-y-4">
                        {/* Featured Image */}
                        <div>
                          <div className="mb-2 flex items-center justify-between">
                            <div className="text-sm font-medium">
                              Featured Image
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setFeaturedImageDialogOpen(true)}
                            >
                              Edit featured image
                            </Button>
                          </div>
                          {event.featuredImage ? (
                            <div className="relative flex min-h-42 items-center justify-center overflow-hidden rounded-md border border-zinc-200 bg-zinc-100">
                              <CloudinaryImage
                                publicId={event.featuredImage.cloudinaryId}
                                mode="original"
                                className="h-64 w-full object-contain"
                              />
                              <Button
                                variant="outline"
                                size="icon"
                                className="absolute top-2 right-2"
                                disabled={updateImageMutation.isPending}
                                onClick={() =>
                                  updateImageMutation.mutate(
                                    { featuredImageId: null },
                                    {
                                      onSuccess: () =>
                                        toastSuccess('Featured image removed.'),
                                    },
                                  )
                                }
                              >
                                <Trash2Icon className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex h-32 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 text-center">
                              <div className="text-sm text-zinc-500">
                                No featured image set
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Gallery Images */}
                        <div>
                          <div className="mb-2 flex items-center justify-between">
                            <div className="text-sm font-medium">Gallery</div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setGalleryDialogOpen(true)}
                            >
                              Manage images
                            </Button>
                          </div>
                          {event.images && event.images.length > 0 ? (
                            <div className="grid grid-cols-2 gap-2">
                              {event.images.map((image) => (
                                <div
                                  key={image.id}
                                  className="flex min-h-32 items-center justify-center overflow-hidden rounded-md border border-zinc-200 bg-zinc-100"
                                >
                                  <CloudinaryImage
                                    publicId={image.cloudinaryId}
                                    mode="original"
                                    className="h-32 w-full object-contain"
                                  />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="flex h-32 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 text-center">
                              <div className="text-sm text-zinc-500">
                                No photos uploaded
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>

          {event.categories && event.categories.length > 0 && (
            <div className="flex flex-col space-y-8">
              <div className="mb-2">
                <Heading level="h3">Categories</Heading>
              </div>
              <Card>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {event.categories.map((category) => (
                      <span
                        key={category.id}
                        className="rounded-full bg-zinc-100 px-3 py-1 text-sm"
                      >
                        {category.category?.name || 'Unknown'}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {event.tags && event.tags.length > 0 && (
            <div className="flex flex-col space-y-8">
              <div className="mb-2">
                <Heading level="h3">Tags</Heading>
              </div>
              <Card>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {event.tags.map((tag) => (
                      <span
                        key={tag.id}
                        className="rounded-full bg-zinc-100 px-3 py-1 text-sm"
                      >
                        {tag.tag?.name || 'Unknown'}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {event.organizers && event.organizers.length > 0 && (
            <div className="flex flex-col space-y-8">
              <div className="mb-2">
                <Heading level="h3">Organizers</Heading>
              </div>
              <Card>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[100px]">Name</TableHead>
                        <TableHead className="w-[100px]">Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Website</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {event.organizers.map((organizer) => (
                        <TableRow key={organizer.id}>
                          <TableCell>
                            {organizer.organizer?.name || '-'}
                          </TableCell>
                          <TableCell>
                            {organizer.organizer?.email || '-'}
                          </TableCell>
                          <TableCell>
                            {organizer.organizer?.phone || '-'}
                          </TableCell>
                          <TableCell>
                            {organizer.organizer?.website ? (
                              <a
                                href={organizer.organizer.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 underline"
                              >
                                {organizer.organizer.website}
                              </a>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
        <div className="flex flex-col space-y-8 xl:col-span-1">
          <div className="sticky top-4">
            <div className="mb-6 text-center">
              <Heading level="h3">Preview</Heading>

              <div className="mx-auto mt-2 flex max-w-xs items-stretch gap-1 rounded-lg bg-zinc-100 p-1">
                <Button
                  variant={
                    previewMode === 'members-app' ? 'secondary' : 'ghost'
                  }
                  className={cn(
                    'flex-1 hover:bg-white',
                    previewMode === 'members-app' && 'bg-white shadow-md',
                  )}
                  onClick={() => setPreviewMode('members-app')}
                  size="sm"
                >
                  Members App
                </Button>
                <Button
                  variant={
                    previewMode === 'events-site' ? 'secondary' : 'ghost'
                  }
                  className={cn(
                    'flex-1 hover:bg-white',
                    previewMode === 'events-site' && 'bg-white shadow-md',
                  )}
                  onClick={() => setPreviewMode('events-site')}
                  size="sm"
                >
                  Events Site
                </Button>
              </div>
            </div>
            <PhonePreviewContainer>
              {previewMode === 'members-app' ? (
                <EventPreview event={event as any} venue={venue} />
              ) : (
                <EventsSitePreview event={event as any} venue={venue} />
              )}
            </PhonePreviewContainer>
            {/* Preview in new tab button */}
            {previewMode === 'events-site' && (
              <div className="z-10 mt-8 mb-2 flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => {
                    useEventPreviewStore
                      .getState()
                      .setPreviewData({ event: event as any, venue });
                    window.open('/ticketed-events/preview', 'event-preview');
                  }}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Preview in new tab
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Details Sheet */}
      <Sheet
        open={eventDetailsSheetOpen}
        onOpenChange={handleDetailsSheetOpenChange}
      >
        <SheetContent
          onPointerDownOutside={(domEvent) => domEvent.preventDefault()}
          onOpenAutoFocus={(domEvent) => domEvent.preventDefault()}
        >
          <Form {...detailsForm}>
            <form
              onSubmit={detailsForm.handleSubmit(onDetailsSubmit)}
              className="h-full"
            >
              <div className="flex h-full flex-col">
                <SheetHeader>
                  <SheetTitle>{`Edit ${event.name}'s details`}</SheetTitle>
                </SheetHeader>

                <SheetBody className="grow overflow-auto">
                  {detailsForm.formState.errors.root?.message && (
                    <div className="mb-4">
                      <Alert variant="destructive">
                        <AlertTitle>Something went wrong</AlertTitle>
                        <AlertDescription>
                          {detailsForm.formState.errors.root?.message}
                        </AlertDescription>
                      </Alert>
                    </div>
                  )}

                  <div className="space-y-6 py-3">
                    <FormField
                      control={detailsForm.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Event name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Wine & Cheese Evening"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={detailsForm.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description</FormLabel>
                          <FormControl>
                            <Tiptap
                              value={field.value || ''}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              placeholder="Describe your event..."
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={detailsForm.control}
                      name="teaser"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Teaser</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="A short preview of the event."
                              {...field}
                              value={field.value || ''}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={detailsForm.control}
                      name="startDateTime"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Start Date & Time</FormLabel>
                          <FormControl>
                            <Input
                              type="datetime-local"
                              value={field.value || ''}
                              onChange={(domEvent) =>
                                field.onChange(domEvent.target.value)
                              }
                              onBlur={field.onBlur}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={detailsForm.control}
                      name="endDateTime"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>End Date & Time</FormLabel>
                          <FormControl>
                            <Input
                              type="datetime-local"
                              value={field.value || ''}
                              onChange={(domEvent) =>
                                field.onChange(domEvent.target.value)
                              }
                              onBlur={field.onBlur}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={detailsForm.control}
                      name="visibility"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Visibility</FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              value={field.value}
                              className="flex flex-col gap-2"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                  value="PUBLIC"
                                  id="edit-visibility-public"
                                />
                                <label
                                  htmlFor="edit-visibility-public"
                                  className="text-sm leading-none font-normal peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  Public
                                </label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                  value="MEMBERS_ONLY"
                                  id="edit-visibility-members"
                                />
                                <label
                                  htmlFor="edit-visibility-members"
                                  className="text-sm leading-none font-normal peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  Members only
                                </label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                  value="PRIVATE"
                                  id="edit-visibility-private"
                                />
                                <label
                                  htmlFor="edit-visibility-private"
                                  className="text-sm leading-none font-normal peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  Private
                                </label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={detailsForm.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Status</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="DRAFT">Draft</SelectItem>
                              <SelectItem value="PUBLISHED">
                                Published
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </SheetBody>

                <SheetFooter>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={updateDetailsMutation.isPending}
                    disabled={updateDetailsMutation.isPending}
                  >
                    Update
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => handleDetailsSheetOpenChange(false)}
                    disabled={updateDetailsMutation.isPending}
                  >
                    Cancel
                  </Button>
                </SheetFooter>
              </div>
            </form>
          </Form>
        </SheetContent>
      </Sheet>

      <FeaturedImageDialog
        name={event.name}
        resourceType="event"
        currentFeaturedAssetId={event.featuredImageId}
        isOpen={featuredImageDialogOpen}
        onOpenChange={setFeaturedImageDialogOpen}
        onSave={async (assetId) => {
          await updateImageMutation.mutateAsync({ featuredImageId: assetId });
          toastSuccess('Featured image updated successfully.');
        }}
      />

      <ManageGalleryImagesDialog
        name={event.name}
        currentAssetIds={event.images?.map((img) => img.id) ?? []}
        isOpen={galleryDialogOpen}
        onOpenChange={setGalleryDialogOpen}
        onSave={async (assetIds) => {
          await updateImageMutation.mutateAsync({ imageIds: assetIds });
          toastSuccess('Gallery images updated successfully.');
        }}
      />

      <ArchiveEventDialog
        event={event}
        isOpen={eventArchiveDialogOpen}
        setIsOpen={setEventArchiveDialogOpen}
      />

      <RestoreEventDialog
        event={event}
        isOpen={eventRestoreDialogOpen}
        setIsOpen={setEventRestoreDialogOpen}
      />
    </div>
  );
}
